import { Icon } from "common/LegacyIcon";
import { SquareSpinner } from "common/spinners";
import { Tab, TabNav } from "common/TabNav";
import PromisePool from "es6-promise-pool";
import { Map, OrderedSet } from "immutable";
import * as reportActions from "lib/api/rideReports";
import { commonCTAs, commonNouns, fm, Msg } from "lib/localization";
import { photoCenti } from "lib/urlHelpers";
import { getPhotoUploadMaxConcurrency } from "lib/utils";
import React from "react";
import Dropzone from "react-dropzone";
import { FormattedMessage as T, injectIntl } from "react-intl";
import { connect } from "react-redux";
import styles from "./GalleryPicker.scss";

const UPLOADING = "UPLOADING";
const UPLOAD_FAILED = "UPLOAD_FAILED";

const Thumbnail = ({ src, selected, onClick, onDelete }) => {
  const doDelete = (e) => {
    e.stopPropagation();
    onDelete();
  };
  return (
    <div className={styles.Thumbnail} onClick={onClick}>
      <img src={src} />
      {selected && <div className={styles.checkboxBadge} />}
      {onDelete && (
        <div className={styles.deleteBadge} onClick={doDelete}>
          <Icon i="x" size={14} />
        </div>
      )}
    </div>
  );
};

export class GalleryPicker extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selected: OrderedSet(props.defaultSelected),
      uploadsPending: 0,
      uploadsCompleted: 0,
      uploadsFailed: 0,
      operation: null,
      selectedTab: props.ridePhotos && props.ridePhotos.size > 0 ? "ride" : "upload",
    };
  }

  /*
  so the issue here is that uploaded photos need to be tracked kind of seperately than
  photos that we're pulling in from the ride, because the user needs to be able to
  delete them and whatnot.
  */
  onDrop = (files) => {
    const { resetEditorMode } = this.props;
    resetEditorMode(); // pull the editor out of drag mode, since it doesn't get the drop event

    const { addPhoto, reportId } = this.props;
    this.setState({
      operation: UPLOADING,
      selectedTab: "upload",
      uploadsPending: files.length,
      uploadsFailed: 0,
      uploadsTotal: files.length,
    });

    const promiseProducer = () => {
      const nextUpload = files.shift();
      if (!nextUpload) {
        return null;
      }
      return addPhoto(reportId, nextUpload)
        .then((result) => {
          this.setState({
            uploadsPending: this.state.uploadsPending - 1,
            selected: this.state.selected.add(result.value.photo.id),
          });
        })
        .catch((e) => {
          this.setState({
            uploadsPending: this.state.uploadsPending - 1,
            uploadsFailed: this.state.uploadsFailed + 1,
          });
        });
    };

    // I don't like the API of this thing, but it's small, well-tested, and works
    // so it didn't really make sense to write our own here
    const pool = new PromisePool(promiseProducer, getPhotoUploadMaxConcurrency());

    pool
      .start()
      .then(() => {
        // all photos uploaded without problems
        const { uploadsFailed } = this.state;
        this.setState({ operation: uploadsFailed ? UPLOAD_FAILED : null });
      })
      .catch(() => {
        // this shouldn't happen because we catch the failed uploads upstream
        // of the promise pool
        this.setState({ operation: UPLOAD_FAILED });
      });
  };

  // only use this with RR photoset photos
  deletePhoto = (photoId) => {
    const { deletePhoto, reportId } = this.props;
    const { selected } = this.state;
    // problem: if other galleries reference this photo,
    // they end up with broken photo entries that can't be removed.
    if (selected.get(photoId)) {
      this.togglePhoto(photoId);
    }
    deletePhoto(reportId, photoId); // todo handle errors here
  };

  togglePhoto = (id) => {
    const { selected } = this.state;
    selected.has(id) ? this.setState({ selected: selected.delete(id) }) : this.setState({ selected: selected.add(id) });
  };

  done = () => {
    const { ridePhotos, reportPhotos } = this.props;
    const { selected } = this.state;
    const imageIds = selected.toArray();
    const combinedPhotos = ridePhotos.concat(reportPhotos);
    const photoHash = Map(combinedPhotos.map((p) => p.get("id")).zip(combinedPhotos));
    const getPhoto = (id) => photoHash.get(id);
    const getAspect = (p) => p.width / p.height;
    const candidateAspect = selected
      .map(getPhoto)
      .map(getAspect)
      .filter((n) => Number.isFinite(n))
      .reduce((a, b) => (a < b ? a : b), Infinity);

    // sanity-check aspect
    const minAspect = isFinite(candidateAspect) && candidateAspect > 0 ? candidateAspect : null;

    return this.props.onChange({ imageIds, minAspect });
  };

  openFilePicker = () => {
    this._dropzone && this._dropzone.open();
  };

  setDropRef = (component) => {
    this._dropzone = component;
  };

  openFilePicker = () => {
    this._dropzone && this._dropzone.open();
  };

  setTab = (selectedTab) => this.setState({ selectedTab });

  // TODO FIXME switch this stuff from janky flexbox vertical layout to fixed-height
  // thumbnail container and tab container
  // TODO fixme style the picker differently when something is about to be dropped on it
  // (like gmail compose does)
  render() {
    const { selected, operation, selectedTab, uploadsPending, uploadsFailed, uploadsTotal } = this.state;
    const { ridePhotos, reportPhotos, intl } = this.props;
    const displayPhotos = selectedTab === "ride" ? ridePhotos : reportPhotos;
    const brokenPhotos = selected.subtract(
      ridePhotos.map((p) => p.get("id")),
      reportPhotos.map((p) => p.get("id"))
    ); // find selected photos that aren't in ridePhotos or reportPhotos
    const hasSelected = selected.size !== 0;

    return (
      <Dropzone
        disableClick
        className={styles.GalleryPicker}
        onDrop={this.onDrop}
        accept="image/*"
        multiple
        ref={this.setDropRef}
      >
        <TabNav onChange={this.setTab} selected={selectedTab}>
          <Tab id="upload">
            <T id="GalleryPicker.reportPhotos" defaultMessage="Report Photos" />
          </Tab>
          <Tab id="ride">
            <T id="GalleryPicker.ridePhotos" defaultMessage="Ride Photos" />
          </Tab>
        </TabNav>
        <div className={styles.thumbContainer}>
          {displayPhotos.slice(0, 200).map((p) => (
            <Thumbnail
              src={photoCenti(p.get("id"))}
              onDelete={selectedTab === "upload" && (() => this.deletePhoto(p.get("id")))}
              onClick={() => this.togglePhoto(p.get("id"))}
              key={p.get("id")}
              selected={selected.has(p.get("id"))}
            />
          ))}
          {brokenPhotos.map((id) => (
            <Thumbnail selected src={photoCenti()} onClick={() => this.togglePhoto(id)} />
          ))}
        </div>
        <div className={styles.upload} onClick={this.openFilePicker}>
          <div className={styles.uploadContent}>
            {operation === UPLOADING && (
              <LoadingIndicator pending={uploadsPending} failed={uploadsFailed} total={uploadsTotal} />
            )}
            {operation !== UPLOADING && (
              <div>
                <Icon i="upload" size={50} />
                <p>
                  <T
                    id="GalleryPicker.pickAbove"
                    defaultMessage="Pick your photo(s) above,{br}or drag here to upload"
                    values={{ br: <br /> }}
                  />
                </p>
              </div>
            )}
            {operation === UPLOAD_FAILED && (
              <p className={styles.error}>
                <T
                  id="GalleryPicker.uploadError"
                  defaultMessage="Oops! {uploadsFailed} {someImages} failed to upload. Please try again or contact support."
                  values={{
                    uploadsFailed,
                    someImages: fm(intl, commonNouns.images, {
                      count: uploadsFailed,
                    }),
                  }}
                />
              </p>
            )}
          </div>
        </div>
        {!hasSelected && (
          <p className={styles.disabledHint}>
            <T id="GalleryPicker.atLeastOne" defaultMessage="you must select at least one photo to save this block" />
          </p>
        )}
        <button className={styles.done} onClick={this.done} disabled={!hasSelected}>
          <Msg m={commonCTAs.done} /> &raquo;
        </button>
      </Dropzone>
    );
  }
}

const LoadingIndicator = ({ pending, failed, total }) => (
  <div className={styles.loadingIndicator}>
    <SquareSpinner size={50} />
    <p>
      Uploading ({total - pending - failed}/{total})
    </p>
  </div>
);

const mapDispatchToProps = {
  addPhoto: reportActions.addPhoto,
  deletePhoto: reportActions.deletePhoto,
};

export const GalleryPickerContainer = connect(null, mapDispatchToProps)(injectIntl(GalleryPicker));
